import type { FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Button from '@components/Button';
import { Spinner } from '@components/Spinner';
import { fetchEvalConfig } from '@queries/evalConfigs';
import Nav from '@src/routes';
import { useQuery } from '@tanstack/react-query';

import { getEvalConfigQueryKey } from '../constants';
import { useEvalConfigMutations } from '../hooks/useEvalConfigMutations';
import { EvalConfigView } from '../views/EvalConfig';

export const EvalConfigController: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: id ? getEvalConfigQueryKey(id) : ['eval-config', 'missing'],
    queryFn: () => fetchEvalConfig(id!),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
  });

  const { onSaveMeta, onSaveDraft, onPublish, isSavingMeta, isSavingDraft, isPublishing } = useEvalConfigMutations(
    id ?? '',
  );

  if (!id) {
    navigate(Nav.NotFound, { replace: true });
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="mx-auto flex max-w-md flex-col items-center gap-4 px-4 py-16 text-center">
        <h2 className="text-info-900 text-lg font-medium">Eval config not found</h2>
        <p className="text-info-500 text-sm">The config may have been removed or you may not have access.</p>
        <Button onClick={() => navigate('/eval-configs')}>Back to list</Button>
      </div>
    );
  }

  return (
    <EvalConfigView
      detail={data}
      isSavingMeta={isSavingMeta}
      isSavingDraft={isSavingDraft}
      isPublishing={isPublishing}
      onSaveMeta={onSaveMeta}
      onSaveDraft={onSaveDraft}
      onPublish={onPublish}
    />
  );
};
